import { ImagePaths } from '../../lib/imageUrls.js';

export const purviewEndpointSecurity = {

    id: 48,
    slug: 'purview-endpoint-security',
    ogImage: '/og/courses/purview-endpoint-security.jpg',
    courseCode: 'PURVIEW-ENDPT-001',
    featured: true,
    title: 'Purview for Endpoint Security',
    subtitle: 'Deploy endpoint DLP across Windows, macOS, and Chrome — with Copilot safeguards',
    organization: 'EDUC4TE',
    valueProposition: 'Deploy multi-platform endpoint Data Loss Prevention across Windows, macOS, and Chrome — plus Microsoft 365 Copilot data protection — in a single intensive day. Aligned to the ASD/ACSC Essential Eight (Patch Applications and Restrict Administrative Privileges) and the Privacy Act 1988, this specialist module hardens data protection at the device edge.',
    duration: '8 hours',
    students: 'New',
    level: 'Intermediate',
    image: ImagePaths.COURSE_3,
    shortDescription: 'PURVIEW-ENDPT-001 — Purview endpoint DLP covering Windows, macOS, Chrome, USB controls, and Copilot safeguards for endpoint security teams.',
    price: {
      aud: 1000,
      currency: 'AUD'
    },

    stripeConfig: {
    priceId: 'price_1Te5C0Ku1nyc322Ud217MkrY',
    productId: 'prod_UdLt3nFoXAq1Ao'
  },

    snapshot: {
      whoItsFor: [
        'Endpoint security teams hardening data protection at the device edge',
        'Microsoft 365 administrators extending Purview DLP to Windows, macOS, and Chrome',
        'Security engineers deploying DLP across multi-platform endpoint estates',
        'Copilot deployment teams configuring prompt safeguards and citation controls',
        'Information security analysts responsible for USB, removable media, and network controls'
      ],
      accreditation: 'Contributes to SC-401 Information Security Administrator certification coverage. Aligned to ASD/ACSC Essential Eight Patch Applications and Restrict Administrative Privileges controls.'
    },

    logistics: {
      classSize: 'One-day intensive — virtual cohort with live lab access (8-16 participants)',
      requirements: [
        'Access to a Microsoft 365 E5 (or Compliance add-on) tenant for hands-on labs',
        'Familiarity with Microsoft Defender for Endpoint and device onboarding',
        'Exposure to endpoint management (Intune or equivalent) and device group scoping',
        'Completion of Module 2 — Data Protection Practitioners, or equivalent production Purview experience',
        'Working knowledge of sensitivity labels and DLP policy concepts'
      ]
    },

    overview: 'Purview for Endpoint Security is Module 3 of the six-part EDUC4TE Purview Training Track. This one-day, 8-hour specialist intensive extends data protection from the Microsoft 365 services to the device edge, enabling security teams to deploy endpoint Data Loss Prevention across Windows 10/11, macOS, and the Chrome browser. You will onboard devices, scope policies with device groups, and integrate Microsoft Defender for Endpoint with Microsoft Purview for unified data protection and just-in-time (JIT) protection. The programme drills into advanced endpoint scenarios — USB blocking and removable media controls, print watermarking, network share protection, and browser restrictions including handling unmanaged browsers — so you can stop data egress at the points where it most often leaks.\n\nThe day closes with comprehensive Microsoft 365 Copilot DLP — configuring prompt safeguards and citation controls, and aligning endpoint and Copilot DLP policy so that classified content is protected consistently whether it leaves via a USB stick, a browser upload, or a Copilot prompt. Throughout, controls are mapped to the ASD/ACSC Essential Eight (Patch Applications and Restrict Administrative Privileges) and the Privacy Act 1988, and the module contributes towards the SC-401 Administering Information Security in Microsoft 365 exam. Module 2 — Data Protection Practitioners (or equivalent production Purview experience) is a prerequisite.',

    outcomes: [
      'Deploy Endpoint DLP across Windows, macOS, and Chrome browser platforms',
      'Integrate Microsoft Defender for Endpoint with Purview for unified data protection',
      'Configure USB blocking, removable media controls, and print watermarking',
      'Implement browser restrictions and network share protection via Endpoint DLP',
      'Configure Microsoft 365 Copilot DLP — prompt safeguards and citation controls'
    ],

    audience: [
      'Endpoint security teams',
      'Microsoft 365 administrators with an endpoint data protection remit',
      'Security engineers deploying DLP across Windows, macOS, and Chrome',
      'Copilot deployment teams',
      'Information security analysts responsible for device-edge controls'
    ],

    prerequisites: [
      'Completion of Module 2 — Data Protection Practitioners, or equivalent production Purview experience',
      'Familiarity with endpoint management and Microsoft Defender for Endpoint',
      'Practical understanding of sensitivity labels and DLP policy authoring',
      'Awareness of the Privacy Act 1988 and ASD/ACSC Essential Eight controls',
      'Access to a Microsoft 365 E5 or Compliance add-on tenant for hands-on labs'
    ],

    modules: [
      {
        number: 1,
        title: 'Endpoint DLP architecture',
        duration: '120 min',
        topics: [
          'Onboard Windows 10/11, macOS, and Chrome to Microsoft Purview Endpoint DLP',
          'Scope policies with device groups and understand policy scoping precedence',
          'Integrate Microsoft Defender for Endpoint with Purview for unified data protection',
          'Validate deployment prerequisites — licensing, device onboarding, and supported platforms',
          'Reference: Microsoft Learn — Get started with Endpoint DLP (updated 2025/2026)'
        ]
      },
      {
        number: 2,
        title: 'Advanced endpoint scenarios',
        duration: '180 min',
        topics: [
          'Configure USB blocking and removable media controls to prevent data egress',
          'Implement network share protection for files copied to file shares',
          'Apply print watermarking and restrict printing of classified content',
          'Configure browser restrictions and handle unmanaged (unsupported) browsers',
          'Enable just-in-time (JIT) protection for files not yet evaluated (Reference: Microsoft Learn — Endpoint DLP, updated 2025/2026)'
        ]
      },
      {
        number: 3,
        title: 'Copilot DLP integration',
        duration: '180 min',
        topics: [
          'Configure Microsoft 365 Copilot DLP prompt safeguards to protect classified content',
          'Implement citation controls to restrict how Copilot surfaces sensitive sources',
          'Align endpoint and Copilot DLP policy for consistent, end-to-end protection',
          'Monitor endpoint and Copilot DLP events using Activity Explorer and DLP alerts',
          'Reference: Microsoft Learn — Endpoint DLP and Copilot DLP (updated 2025/2026)'
        ]
      }
    ],

    format: {
      type: 'One-day intensive (virtual), or lunch-and-learn (8 x 1-hour sessions over 8 days)',
      includes: [
        '8 hours of instructor-led training in a single intensive day',
        'Hands-on endpoint DLP labs across Windows and macOS',
        'USB and network control playbook for removable media and file shares',
        'Microsoft 365 Copilot DLP policy templates — prompt safeguards and citation controls',
        'Certificate of completion suitable for CPD claims'
      ]
    },

    nextIntake: {
      date: 'Register your interest — we will confirm the next cohort',
      details: 'Register interest for the next one-day intensive'
    },

    certification: 'Educ4te Certificate of Completion — Purview for Endpoint Security. This one-day specialist intensive contributes towards preparation for Microsoft Certified: Information Security Administrator Associate (SC-401 Administering Information Security in Microsoft 365).',

    faqs: [
      {
        question: 'Do I need a Microsoft 365 E5 tenant to follow the labs?',
        answer: 'Yes — Endpoint DLP, Defender for Endpoint integration, and Copilot DLP require Microsoft 365 E5 or the Microsoft 365 E5 Compliance add-on. A developer tenant with the Compliance add-on and onboarded devices is sufficient for the labs.'
      },
      {
        question: 'How does this module relate to Module 2 — Data Protection Practitioners?',
        answer: 'Module 2 establishes classification, a four-tier sensitivity label taxonomy, and basic DLP across the core Microsoft 365 services. This module — Module 3 — extends that protection to the device edge with endpoint DLP across Windows, macOS, and Chrome, plus Copilot DLP. Completing Module 2 (or holding equivalent production Purview experience) is a prerequisite.'
      },
      {
        question: 'Is Microsoft Defender for Endpoint required?',
        answer: 'Defender for Endpoint is strongly recommended and is integrated throughout the labs for unified data protection and richer device signals. While some endpoint DLP capabilities operate on onboarded devices without full Defender deployment, the integration scenarios in this module assume Defender for Endpoint is available in your tenant.'
      },
      {
        question: 'What does the Copilot DLP coverage include?',
        answer: 'The Copilot DLP segment covers configuring Microsoft 365 Copilot prompt safeguards and citation controls, and aligning endpoint and Copilot DLP policy so classified content is protected consistently whether it leaves via a device or a Copilot prompt. You receive policy templates you can adapt to your tenant.'
      },
      {
        question: 'How does this course align to the Essential Eight?',
        answer: 'The labs map endpoint controls to the ASD/ACSC Essential Eight — particularly Patch Applications and Restrict Administrative Privileges — and to the Privacy Act 1988 and OAIC APP 11, so you can demonstrate evidence-backed controls over Australian personal information at the device edge.'
      },
      {
        question: 'Is this course eligible for Continuing Professional Development (CPD) claims?',
        answer: 'Yes. Participants receive an Educ4te Certificate of Completion that records the 8 contact hours, learning outcomes, and topics covered. The certificate is suitable for CPD claims under most Australian professional schemes, including IAPP, ISACA, and (ISC)2.'
      },
      {
        question: 'Can this course be delivered as lunch-and-learn sessions?',
        answer: 'Yes. The course runs as eight one-hour sessions over eight days, with the same content and outcomes as the one-day format, paced around the working day so people do not need to be away from their desks for a full day. Delivery is live online or on-site, and private cohorts are available for teams. Hands-on labs are spread across the sessions, so participants need access to the lab environment throughout. Contact us to arrange a schedule.'
      }
    ],

    australianContext: true,
    complianceFrameworks: ['Privacy Act 1988', 'OAIC APP 11', 'ACSC ISM', 'Essential Eight']
};
